import { api } from 'api';
import EditIcon from 'assets/icons/edit-outline-icon.svg';
import CloseIcon from 'assets/ver2/icons/close-blue-icon.svg';
import { getFirstNameLastName } from 'components/common/func';
import EmailIcon from 'components/common/Icons/Email';
import JobIcon from 'components/common/Icons/JobIcon';
import LocationIcon from 'components/common/Icons/LocationIcon';
import PhoneIcon from 'components/common/Icons/PhoneIcon';
import ProfileOutlineIcon from 'components/common/Icons/ProfileOutlineIcon';
import {
  DESKTOP_MIN_WIDTH,
  PROFILE_PREFIX,
  SCREEN_ID_PROFILE_DETAILS,
} from 'constants';
import VerificationRequiresPhotos from 'features/profile2.0/components/VerificationRequiresPhotos';
import { checkMaintenance } from 'helpers/maintenance';
import mask from 'helpers/mask';
import useAuth from 'hooks/useAuth';
import useAutoShowEventModal from 'hooks/useAutoShowEventModal';
import useLang from 'hooks/useLang';
import useQuery from 'hooks/useQuery';
import useStore from 'hooks/useStore';
import useTheme from 'hooks/useTheme';
import { useEffect, useState } from 'react';
import Navbar from 'react-bootstrap/Navbar';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import FormEditProfileAddress from '../../../FormEditProfileAddress';
import FormEditProfileInformation from '../../../FormEditProfileInformation';

const SCREEN_ID = SCREEN_ID_PROFILE_DETAILS;

const PersonalDetailsMobile = () => {
  const { t } = useLang();
  const { isHHMTTheme } = useTheme();
  const { state } = useStore();
  const { currentUser, configOccupation } = state;
  const { firstName, lastName, mobile, email, address } = currentUser || {};
  const { token } = useAuth();

  const [currentJob, setCurrentJob] = useState(null);
  const [
    isShowFormEditProfileInformationPopup,
    setShowFormEditProfileInformationPopup,
  ] = useState(false);
  const [
    isShowFormEditProfileAddressPopup,
    setShowFormEditProfileAddressPopup,
  ] = useState(false);

  const navigate = useNavigate();
  const query = useQuery();
  const tab = query.get('tab');

  const handleOnClose = () => {
    navigate(PROFILE_PREFIX);
  };

  const fetchProfileOccupation = async (token) => {
    try {
      const { data } = await api.getOccupation(token);

      if (data) {
        const occupationInit = configOccupation?.find(
          (o) => o.key === data.job,
        );
        const previousOccupationInit = configOccupation?.find(
          (o) => o.key === data.previousJob,
        );

        setCurrentJob({
          job: occupationInit,
          previousJob: previousOccupationInit,
        });
      }
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);
    }
  };

  const initialProfileInformation = async (token) => {
    await Promise.all([fetchProfileOccupation(token)]);
  };

  useEffect(() => {
    initialProfileInformation(token);
  }, [token, currentUser, configOccupation]);

  const handleOnOpenFormEditProfileInformationPopup = async () => {
    setShowFormEditProfileInformationPopup(true);
  };

  const handleOnCloseFormEditProfileInformationPopup = () => {
    setShowFormEditProfileInformationPopup(false);
  };

  const handleOnOpenFormEditProfileAddressPopup = async () => {
    setShowFormEditProfileAddressPopup(true);
  };

  const handleOnCloseFormEditProfileAddressPopup = () => {
    setShowFormEditProfileAddressPopup(false);
  };

  useAutoShowEventModal({
    screenId: SCREEN_ID,
    params: [],
    modalStyles: {},
    modalOptions: {},
  });

  return (
    <>
      <style>
        {`
          .personal-details-mobile-offcanvas {
            width: 100%!important;
            height: calc(100% - 70px);
            border: none!important;
          }
          .personal-details-mobile-offcanvas-backdrop {
            opacity: 0!important;
          }
        `}
      </style>

      {isShowFormEditProfileInformationPopup && (
        <FormEditProfileInformation
          isOpen={isShowFormEditProfileInformationPopup}
          onClose={handleOnCloseFormEditProfileInformationPopup}
          currentJob={currentJob}
          fetchProfileOccupation={fetchProfileOccupation}
        />
      )}

      {isShowFormEditProfileAddressPopup && (
        <FormEditProfileAddress
          isOpen={isShowFormEditProfileAddressPopup}
          onClose={handleOnCloseFormEditProfileAddressPopup}
        />
      )}

      <PersonalDetailsMobileStyled>
        <NavbarStyled expand={false} expanded={tab} onToggle={handleOnClose}>
          <NavbarOffcanvas
            id="offcanvasNavbar-expand-false"
            aria-labelledby="offcanvasNavbarLabel-expand-false"
            placement="end"
            className="personal-details-mobile-offcanvas"
            backdropClassName="personal-details-mobile-offcanvas-backdrop"
          >
            <OffcanvasHeader closeButton>
              <OffcanvasTitle id="offcanvasNavbarLabel-expand-false">
                {t('label_personal_details')}
              </OffcanvasTitle>
              <img
                src={CloseIcon}
                width={20}
                height={20}
                onClick={handleOnClose}
              />
            </OffcanvasHeader>
            <OffcanvasBody>
              <VerificationRequiresPhotos />
              <Title>
                {t('label_general_information')}{' '}
                <EditIconStyled
                  src={EditIcon}
                  alt="edit-icon"
                  onClick={handleOnOpenFormEditProfileInformationPopup}
                  $isHHMTTheme={isHHMTTheme}
                />
              </Title>
              <FieldWrap>
                <ProfileOutlineIcon
                  color="var(--ds-c-grey-dark)"
                  size={{ width: 24, height: 24 }}
                />
                <FieldValue>
                  {getFirstNameLastName({
                    firstName,
                    lastName,
                  })}
                </FieldValue>
              </FieldWrap>
              <FieldWrap>
                <EmailIcon size={{ width: 24, height: 24 }} />
                <FieldValue>{email ? mask.email(email) : '-'}</FieldValue>
              </FieldWrap>
              <FieldWrap>
                <PhoneIcon size={{ width: 24, height: 24 }} />
                <FieldValue>{mobile ? mask.phone(mobile) : '-'}</FieldValue>
              </FieldWrap>
              {currentJob?.job && (
                <FieldWrap>
                  <JobIcon size={{ width: 24, height: 24 }} />
                  <FieldValue>
                    {currentJob?.previousJob
                      ? `${currentJob?.job?.value} (${currentJob?.previousJob?.value})`
                      : currentJob?.job?.value}
                  </FieldValue>
                </FieldWrap>
              )}
              <Divider />
              <Title>
                {t('label_residential_address')}{' '}
                <EditIconStyled
                  src={EditIcon}
                  alt="edit-icon"
                  onClick={handleOnOpenFormEditProfileAddressPopup}
                  $isHHMTTheme={isHHMTTheme}
                />
              </Title>
              <FieldWrap>
                <LocationIcon size={{ width: 24, height: 24 }} />
                <FieldValue style={{ textTransform: 'uppercase' }}>
                  {address?.fullAddress}
                </FieldValue>
              </FieldWrap>
            </OffcanvasBody>
          </NavbarOffcanvas>
        </NavbarStyled>
      </PersonalDetailsMobileStyled>
    </>
  );
};

const PersonalDetailsMobileStyled = styled.div`
  position: relative;
`;

const NavbarStyled = styled(Navbar)`
  display: initial;
  position: absolute;
  top: -40px;
  right: 0;
  padding: 0;
  height: 40px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    top: unset;
    right: unset;
  }
`;

const NavbarOffcanvas = styled(Navbar.Offcanvas)`
  margin-top: 70px;
`;
const OffcanvasHeader = styled(Offcanvas.Header)`
  min-height: 40px;
  padding: 0 16px;
  border-bottom: 1px solid #ececf0;

  display: flex;
  justify-content: space-between;

  & button {
    display: none;
  }

  & span {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    color: var(--ds-c-sky);
    cursor: pointer;
    user-select: none;

    &:hover {
      color: var(--ds-c-sky-hover);
    }
  }
`;
const OffcanvasTitle = styled(Offcanvas.Title)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  text-transform: uppercase;
  color: #000;
`;
const OffcanvasBody = styled(Offcanvas.Body)`
  padding-inline: 16px;
  padding-block: 0px;
  padding-top: 16px;
  padding-bottom: 24px;
`;

const FieldWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  width: 100%;
  height: fit-content;
  min-height: 40px;
  margin-top: 8px;
  padding: 10px 0;
`;
const FieldValue = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);

  width: 100%;
  margin: 0;
  padding: 0;
  padding-left: 16px;

  & span {
    color: #7a8189;
  }
`;
const Title = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  color: #0e1012;

  display: flex;
  align-items: center;
`;
const EditIconStyled = styled.img`
  margin-left: 8px;
  filter: ${(props) =>
    props.$isHHMTTheme ? 'var(--ds-f-c-yellow)' : 'var(--ds-f-c-blue)'};
  cursor: pointer;
`;

const Divider = styled.div`
  border-bottom: 1px solid #eef2f5;
  margin-bottom: 24px;
  margin-top: 24px;
`;

export default PersonalDetailsMobile;
